import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowRight, Home, FileText, DollarSign, FileCheck, Hammer, Key } from 'lucide-react';
import { buildProcess } from '../data/mockData';

const BuildOnYourLot = () => {
  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto mb-16">
            <h1 className="text-5xl md:text-6xl font-bold text-[#262626] mb-6">
              Build on Your Lot
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Transform your land into your dream home with our comprehensive build-on-your-lot service. We handle every detail from lot evaluation to final walkthrough.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {[
              {
                title: 'Own Your Land',
                description: 'You control the location, timing, and investment',
                icon: Home
              },
              {
                title: 'Build Equity',
                description: 'Gain 10-20% equity upon completion',
                icon: FileText
              },
              {
                title: 'Fixed Pricing',
                description: 'Transparent costs with no surprises',
                icon: DollarSign
              }
            ].map((benefit, index) => (
              <Card key={index} className="border-2 border-gray-100 hover:border-[#DAA520] transition-all duration-300">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-[#DAA520]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <benefit.icon className="text-[#DAA520]" size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-[#262626] mb-3">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works - Step by Step */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#262626] mb-4">
              How The Process Works
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From initial evaluation to move-in day, we guide you through every phase
            </p>
          </div>

          <div className="space-y-12">
            {buildProcess.map((step, index) => {
              const icons = [Home, FileText, DollarSign, FileCheck, Hammer, Key];
              const Icon = icons[index];
              const isEven = index % 2 === 0;

              return (
                <div key={step.step} className={`flex flex-col ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'} gap-8 items-center`}>
                  <div className="flex-1">
                    <Card className="border-2 border-[#DAA520] shadow-lg">
                      <CardContent className="p-8">
                        <div className="flex items-start space-x-4">
                          <div className="w-16 h-16 bg-[#DAA520] text-white rounded-full flex items-center justify-center text-2xl font-bold flex-shrink-0">
                            {step.step}
                          </div>
                          <div>
                            <h3 className="text-2xl font-bold text-[#262626] mb-3">{step.title}</h3>
                            <p className="text-gray-600 leading-relaxed text-lg">{step.description}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                  <div className="flex-1 flex justify-center">
                    <div className="w-32 h-32 bg-[#DAA520]/10 rounded-full flex items-center justify-center">
                      <Icon className="text-[#DAA520]" size={64} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Construction Timeline Visual */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#262626] mb-4">
              Typical Construction Timeline
            </h2>
            <p className="text-xl text-gray-600">
              From groundbreaking to move-in day
            </p>
          </div>

          <div className="relative">
            <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-[#DAA520]/30"></div>
            
            <div className="space-y-8">
              {[
                { phase: 'Pre-Construction', duration: '2-4 weeks', activities: ['Permits', 'Site prep', 'Final planning'] },
                { phase: 'Foundation', duration: '2-3 weeks', activities: ['Excavation', 'Footers', 'Foundation walls'] },
                { phase: 'Framing', duration: '3-4 weeks', activities: ['Floor system', 'Wall framing', 'Roof structure'] },
                { phase: 'Mechanicals', duration: '3-4 weeks', activities: ['Plumbing', 'HVAC', 'Electrical'] },
                { phase: 'Finishing', duration: '6-8 weeks', activities: ['Drywall', 'Trim', 'Flooring', 'Cabinets'] },
                { phase: 'Final', duration: '1-2 weeks', activities: ['Inspections', 'Walkthrough', 'Move-in'] }
              ].map((timeline, index) => (
                <div key={index} className="relative md:w-1/2 md:mx-auto">
                  <Card className="border-2 border-gray-200 hover:border-[#DAA520] transition-all duration-300">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-xl font-bold text-[#262626]">{timeline.phase}</h3>
                        <span className="text-[#DAA520] font-semibold bg-[#DAA520]/10 px-3 py-1 rounded-full text-sm">
                          {timeline.duration}
                        </span>
                      </div>
                      <ul className="space-y-1">
                        {timeline.activities.map((activity, i) => (
                          <li key={i} className="text-gray-600 flex items-center">
                            <span className="w-1.5 h-1.5 bg-[#DAA520] rounded-full mr-2"></span>
                            {activity}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                  <div className="hidden md:block absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-4 h-4 bg-[#DAA520] rounded-full border-4 border-white shadow-lg"></div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-center mt-12">
            <p className="text-gray-600 mb-6">
              <strong>Total Timeline:</strong> Approximately 4-6 months from groundbreaking to completion
            </p>
          </div>
        </div>
      </section>

      {/* Why Build on Your Lot */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#262626] mb-4">
              Why Build on Your Own Lot?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: 'Location Control',
                description: 'Choose exactly where you want to live – near family, work, or in your favorite neighborhood.',
                benefit: 'Perfect location'
              },
              {
                title: 'Investment Advantage',
                description: 'Build significant equity from day one. Typical gains of 10-20% upon completion.',
                benefit: 'Immediate equity'
              },
              {
                title: 'Design Freedom',
                description: 'Customize your home to match your lifestyle, not compromise on someone else\'s choices.',
                benefit: 'Your vision'
              },
              {
                title: 'Quality Assurance',
                description: 'Be involved in every phase. Know exactly what materials and methods go into your home.',
                benefit: 'Peace of mind'
              }
            ].map((item, index) => (
              <Card key={index} className="border-2 border-gray-100 hover:border-[#DAA520] transition-all duration-300 hover:shadow-xl">
                <CardContent className="p-8">
                  <div className="flex items-start justify-between mb-4">
                    <h3 className="text-2xl font-bold text-[#262626]">{item.title}</h3>
                    <span className="text-[#DAA520] text-sm font-semibold bg-[#DAA520]/10 px-3 py-1 rounded-full">
                      {item.benefit}
                    </span>
                  </div>
                  <p className="text-gray-600 leading-relaxed">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#DAA520]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Evaluate Your Lot?
          </h2>
          <p className="text-xl mb-8 text-white/90">
            Let's discuss your lot's potential and how we can bring your vision to life.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-[#262626] hover:bg-black text-white px-8 py-6 text-lg">
                Schedule Lot Evaluation
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
            <Link to="/floor-plans">
              <Button variant="outline" className="bg-white/10 backdrop-blur-md border-white text-white hover:bg-white hover:text-[#262626] px-8 py-6 text-lg">
                Browse Floor Plans
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BuildOnYourLot;
